import type { ExpressionSpecification, FilterSpecification, SymbolLayerSpecification } from 'mapbox-gl';
import { Layer } from 'react-map-gl/mapbox';
import { COLOR_NEUTRAL_WHITE, COLOR_TEXT_PRIMARY, COLOR_TEXT_SECONDARY } from '@alltrails/denali/tokens';
import useMap from '../../hooks/useMap';
import {
  buildTrailheadCollapsedIconImage,
  buildTrailheadCountTextField,
  buildTrailheadIconCollapsedOffset,
  buildTrailheadIconImage,
  buildTrailheadIconOffset,
  buildTrailheadNameTextField,
  buildTrailheadNameTextOffset,
  buildTrailheadSelectedIconImage,
  buildTrailheadSortKey,
  buildTrailheadTextOffset,
  type TrailheadVariantExpressions
} from '../../utils/buildTrailheadIconExpressions';
import {
  getLayerBeforeId,
  trailheadsOverlayCollapsedActiveId,
  trailheadsOverlayCollapsedId,
  trailheadsOverlayLayerId,
  trailheadsOverlayNamesId
} from '../../utils/layers';
import { getTrailheadPinStyle, type TrailheadsExploreVariant } from '../../utils/trailheadPinVariant';

type TrailheadVectorPinLayerProps = {
  collapsedActiveLayerId?: string;
  collapsedLayerId?: string;
  filter?: ExpressionSpecification;
  isCollapsedExpression: ExpressionSpecification;
  isHoveredExpression: ExpressionSpecification;
  isSelectedExpression: ExpressionSpecification;
  layerId?: string;
  pinVariant?: TrailheadsExploreVariant;
  nameLayerId?: string;
  popularityExpression?: ExpressionSpecification | number;
  sourceId: string;
  sourceLayer?: string;
  trailCountExpression: ExpressionSpecification;
} & TrailheadVariantExpressions;

// https://www.figma.com/design/Hfw8SV8IylHR4A3nnQ7Fvs/Map-Icons?node-id=481-1698&p=f&m=dev

// From here on the map is zoomed in far enough that no pin is collapsed (see pinZoomPopularityMap), so every pin shows its name.
const trailheadNamesAlwaysShownFromZoom = 15;

const TrailheadVectorPinLayer = ({
  collapsedActiveLayerId = trailheadsOverlayCollapsedActiveId,
  collapsedLayerId = trailheadsOverlayCollapsedId,
  filter,
  isClosedExpression,
  isCollapsedExpression,
  isCompletedExpression,
  isHoveredExpression,
  isSavedExpression,
  isSelectedExpression,
  layerId = trailheadsOverlayLayerId,
  pinVariant,
  nameLayerId = trailheadsOverlayNamesId,
  popularityExpression = 0,
  sourceId,
  sourceLayer,
  trailCountExpression
}: TrailheadVectorPinLayerProps) => {
  const map = useMap();
  const pinStyle = getTrailheadPinStyle(pinVariant);
  const variantExpressions = { isClosedExpression, isCompletedExpression, isSavedExpression };
  const isActiveExpression: ExpressionSpecification = ['any', isHoveredExpression, isSelectedExpression];
  const sourceLayerProps = sourceLayer === undefined ? {} : { 'source-layer': sourceLayer };
  const sharedLayoutProps: SymbolLayerSpecification['layout'] = {
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
    'text-allow-overlap': true,
    'text-ignore-placement': true,
    'text-font': ['AllTrails Aeonik Medium', 'Manrope Bold'],
    'text-line-height': 1,
    'symbol-sort-key': buildTrailheadSortKey(isActiveExpression, popularityExpression, variantExpressions),
    'text-field': buildTrailheadCountTextField(isActiveExpression, trailCountExpression),
    'text-offset': buildTrailheadTextOffset(variantExpressions, isActiveExpression),
    'icon-size': ['interpolate', ['linear'], ['zoom'], 10, 0.7, 14, 1.0],
    'text-size': ['interpolate', ['linear'], ['zoom'], 10, 11, 14, 12]
  };
  // Cluster tiles come with their own `point_count`, which these pins never draw, since a trailhead
  // already counts its trails.
  const pinFilter = (...conditions: ExpressionSpecification[]) =>
    ['all', ['!', ['has', 'point_count']], ...conditions, ...(filter === undefined ? [] : [filter])] as FilterSpecification;

  const textColor: ExpressionSpecification = ['case', isClosedExpression, COLOR_TEXT_SECONDARY, COLOR_TEXT_PRIMARY];

  // The count owns an expanded pin's `text-field` and its offset, so the name needs a layer of its own.
  // It shares the pins' sort key so names stack in the same order as the pins, and the layer sits just
  // below the pins so a pin covers its neighbors' names. It also writes the name of a hovered or selected
  // pin, collapsed or not, which the pin layers leave blank so the name never hops between layers.
  const namePaintProps: SymbolLayerSpecification['paint'] = {
    'text-color': textColor,
    'text-halo-color': COLOR_NEUTRAL_WHITE,
    'text-halo-width': 1.5
  };

  return (
    <>
      {/* Trailhead names, below the pins. This layer is added first: when none of these layers are on the map
          yet, they all resolve the same `beforeId`, and a layer added later lands above one added earlier. */}
      <Layer
        beforeId={getLayerBeforeId(map, nameLayerId)}
        filter={pinFilter()}
        id={nameLayerId}
        layout={{
          ...sharedLayoutProps,
          'text-field': buildTrailheadNameTextField(isActiveExpression, isCollapsedExpression, trailheadNamesAlwaysShownFromZoom),
          'text-offset': buildTrailheadNameTextOffset(trailCountExpression)
        }}
        paint={namePaintProps}
        source={sourceId}
        {...sourceLayerProps}
        type="symbol"
      />
      {/* Expanded pins */}
      <Layer
        beforeId={getLayerBeforeId(map, layerId)}
        filter={pinFilter(['!', isCollapsedExpression])}
        id={layerId}
        layout={{
          ...sharedLayoutProps,
          'icon-image': buildTrailheadIconImage(variantExpressions, isActiveExpression, trailCountExpression, pinStyle),
          'icon-offset': buildTrailheadIconOffset(isActiveExpression)
        }}
        paint={{ 'text-color': textColor }}
        source={sourceId}
        {...sourceLayerProps}
        type="symbol"
      />
      {/* Collapsed pins. */}
      <Layer
        beforeId={getLayerBeforeId(map, collapsedLayerId)}
        filter={pinFilter(isCollapsedExpression, ['!', isActiveExpression])}
        id={collapsedLayerId}
        layout={{
          ...sharedLayoutProps,
          'icon-image': buildTrailheadCollapsedIconImage(variantExpressions),
          'text-field': ''
        }}
        source={sourceId}
        {...sourceLayerProps}
        type="symbol"
      />
      {/* A collapsed pin expands to the selected pin on its own layer so it draws over its neighbors. */}
      <Layer
        beforeId={getLayerBeforeId(map, collapsedActiveLayerId)}
        filter={pinFilter(isCollapsedExpression, isActiveExpression)}
        id={collapsedActiveLayerId}
        layout={{
          ...sharedLayoutProps,
          'icon-image': buildTrailheadSelectedIconImage(variantExpressions, pinStyle),
          'icon-offset': buildTrailheadIconCollapsedOffset(isActiveExpression),
          'text-field': ''
        }}
        source={sourceId}
        {...sourceLayerProps}
        type="symbol"
      />
    </>
  );
};

export default TrailheadVectorPinLayer;
